import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, message, Button as AntButton } from 'antd';
import koKR from 'antd/locale/ko_KR';
import {
  getOrderInfo,
  getOrderSaleStatus,
  getReservationTravelerListBySaleNo,
  getReservationSaleInfo,
  saveReservationTravelerChanges,
  getCustomerListBySaleNoFromRCT,
} from '../services/apiService';
import OtherCompanyOrderPricePanel from '../components/OtherCompanyOrderPricePanel';
import TravelerAccordionCard from '../components/TravelerAccordionCard';
import { mergeTravelersWithRctPassport } from '../utils/reservationTravelerHelpers';

const productNameFallback = '주문정보';

export default function OtherCompanyReservationDetail() {
  const { orderNo } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [saleStatusRow, setSaleStatusRow] = useState(null);
  const [saleNo, setSaleNo] = useState(null);

  const [travelerLoading, setTravelerLoading] = useState(false);
  const [travelerError, setTravelerError] = useState(null);
  const [travelers, setTravelers] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [editLocked, setEditLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const listContext = useMemo(() => {
    const sp = new URLSearchParams(location?.search || '');
    return {
      productName: location?.state?.productName || sp.get('productName') || '',
      supplierName: location?.state?.supplierName || sp.get('supplierName') || '',
      departDate: location?.state?.departDate || sp.get('departDate') || '',
      city: location?.state?.city || sp.get('city') || '',
      country: location?.state?.country || sp.get('country') || '',
    };
  }, [location?.state, location?.search]);

  const productName =
    listContext.productName ||
    orderInfo?.판매상품명 ||
    productNameFallback;

  const isCancelled =
    saleStatusRow?.취소여부 === 1 ||
    String(saleStatusRow?.취소여부) === '1';

  const refreshOrderInfo = useCallback(async () => {
    if (!orderNo) return;
    try {
      const info = await getOrderInfo({ orderNo });
      setOrderInfo(info || null);
    } catch (e) {
      console.warn('[OtherCompanyReservationDetail] failed to refresh order info', e);
    }
  }, [orderNo]);

  const reloadTravelers = useCallback(async () => {
    if (!saleNo) {
      setTravelers([]);
      return;
    }
    const list = await getReservationTravelerListBySaleNo({ saleNo });
    const merged = await mergeTravelersWithRctPassport({
      saleNo,
      list,
      getReservationTravelerListBySaleNo,
      getCustomerListBySaleNoFromRCT,
    });
    setTravelers(merged);
    return merged;
  }, [saleNo]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!orderNo) return;
      try {
        setLoading(true);
        setError(null);
        const [info, status] = await Promise.all([
          getOrderInfo({ orderNo }),
          getOrderSaleStatus({ orderNo }).catch(() => null),
        ]);
        if (!mounted) return;
        setOrderInfo(info || null);
        setSaleStatusRow(status || null);
        const resolved =
          status?.판매번호 ??
          status?.주문판매번호 ??
          status?.saleNo ??
          location?.state?.saleNo ??
          new URLSearchParams(location?.search || '').get('saleNo') ??
          null;
        setSaleNo(resolved != null && String(resolved).trim() !== '' ? resolved : null);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || '주문 정보를 불러오지 못했습니다.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [orderNo, location?.state?.saleNo, location?.search]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!saleNo) {
        setTravelers([]);
        setEditLocked(false);
        return;
      }
      try {
        setTravelerLoading(true);
        setTravelerError(null);
        const saleRow = await getReservationSaleInfo({ saleNo }).catch(() => null);
        if (!mounted) return;
        if (saleRow && typeof saleRow.명단잠금여부 !== 'undefined') {
          setEditLocked(!!saleRow.명단잠금여부);
        } else {
          setEditLocked(false);
        }
        const merged = await reloadTravelers();
        if (!mounted) return;
        if (merged?.length > 0) {
          setExpandedKeys([merged[0].key]);
        }
      } catch (e) {
        if (!mounted) return;
        setTravelerError(e?.message || '여행객 정보를 불러오지 못했습니다.');
      } finally {
        if (mounted) setTravelerLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [saleNo, reloadTravelers]);

  const handleSave = async (customTravelers) => {
    if (!saleNo) return;
    try {
      setSaving(true);
      const fieldRows = [];
      const amountRows = [];
      const orderRows = [];
      const source = Array.isArray(customTravelers) ? customTravelers : travelers;

      source.forEach((t) => {
        const travelerNo = Number(t?.여행객번호 || t?.여행객키);
        if (!travelerNo) return;
        const pushField = (fieldNo, val) => fieldRows.push({ 여행객번호: travelerNo, 필드번호: fieldNo, 값: val });
        pushField(8, t?.한글이름 ?? null);
        pushField(-1, t?.룸번호 ?? null);
        pushField(109, t?.성별 ?? null);
        pushField(9, t?.영문성 ?? null);
        pushField(10, t?.영문이름 ?? null);
        pushField(11, t?.생년월일 ?? null);
        pushField(13, null);
        pushField(253, t?.여권번호 ?? null);
        pushField(254, t?.여권만료일 ?? null);
        pushField(314, t?.여행객비고 ?? null);
        pushField(12, t?.휴대폰 ?? null);
        pushField(256, t?.이메일 ?? null);
        pushField(404, null);
        pushField(255, null);

        amountRows.push({
          여행객번호: travelerNo,
          상품금액: Number(t?.상품금액원화) || 0,
          공급단가: Number(t?.공급단가) || 0,
          택스및유류: Number(t?.택스및유류원화) || 0,
        });

        orderRows.push({
          여행객번호: travelerNo,
          여행객종류: t?.여행객종류 ?? '여행객',
          선발권동의: !!t?.선발권동의,
          명단확인: !!t?.명단확인,
          순번: Number.isFinite(Number(t?.순번)) ? Number(t?.순번) : orderRows.length + 1,
        });
      });

      await saveReservationTravelerChanges({
        saleNo,
        travelerFieldRows: fieldRows,
        travelerAmounts: amountRows,
        travelerOrders: orderRows,
      });
      message.success('저장되었습니다.');
      await reloadTravelers();
      await refreshOrderInfo();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfigProvider locale={koKR}>
      <div className="p-4 space-y-4 max-w-[1600px] mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            className="text-sm text-gray-600 hover:text-blue-700"
            onClick={() => navigate('/settlements')}
          >
            ← 정산 · 타사상품 판매조회
          </button>
          <span className="text-xs text-gray-500 bg-gray-50 border border-gray-200 px-2 py-1 rounded">
            타사상품 판매 상세
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-10 gap-4">
          <div className="lg:col-span-7 space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <h1 className="text-xl font-semibold text-gray-900 line-clamp-2 mb-2">{productName}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                <span>
                  주문번호{' '}
                  <span className="font-mono font-medium text-gray-800">{orderNo}</span>
                </span>
                {listContext.supplierName && (
                  <span>
                    공급업체{' '}
                    <span className="font-medium text-gray-800">{listContext.supplierName}</span>
                  </span>
                )}
                {saleStatusRow?.판매상태 && (
                  <span>
                    판매상태{' '}
                    <span className="font-medium text-gray-800">{saleStatusRow.판매상태}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-800 mb-3">주문 정보</h2>
              {loading && <p className="text-sm text-gray-500">불러오는 중...</p>}
              {!loading && error && <p className="text-sm text-red-600">{error}</p>}
              {!loading && !error && orderInfo && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <dt className="text-gray-500">주문자</dt>
                    <dd className="text-gray-900">{orderInfo.주문자 || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">주문자 휴대폰</dt>
                    <dd className="text-gray-900 font-mono">{orderInfo.주문자휴대폰 || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">주문일</dt>
                    <dd className="text-gray-900">{orderInfo.주문일 || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">주문경로</dt>
                    <dd className="text-gray-900">{orderInfo.주문경로 || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">대리점</dt>
                    <dd className="text-gray-900">{orderInfo.대리점 || '-'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">등록자</dt>
                    <dd className="text-gray-900">{orderInfo.등록자 || '-'}</dd>
                  </div>
                  {(listContext.departDate || listContext.city || listContext.country) && (
                    <>
                      {listContext.departDate && (
                        <div>
                          <dt className="text-gray-500">출발일</dt>
                          <dd className="text-gray-900">{listContext.departDate}</dd>
                        </div>
                      )}
                      {(listContext.city || listContext.country) && (
                        <div>
                          <dt className="text-gray-500">여행지</dt>
                          <dd className="text-gray-900">
                            {[listContext.country, listContext.city].filter(Boolean).join(' · ') || '-'}
                          </dd>
                        </div>
                      )}
                    </>
                  )}
                </dl>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-sm font-semibold text-gray-800">여행객</h2>
                  <span className="text-xs text-gray-500">{travelers.length}명</span>
                  {editLocked && (
                    <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                      명단편집 불가
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {travelerLoading && <span className="text-xs text-gray-500">불러오는 중...</span>}
                  {saleNo && !editLocked && (
                    <AntButton type="primary" size="small" loading={saving} onClick={() => handleSave()}>
                      저장
                    </AntButton>
                  )}
                </div>
              </div>
              <div className="p-4">
                {!saleNo && !travelerLoading && (
                  <p className="text-sm text-gray-500">판매번호를 확인할 수 없어 여행객 목록을 불러오지 못했습니다.</p>
                )}
                {travelerError && <p className="text-sm text-red-600">{travelerError}</p>}
                {!travelerLoading && !travelerError && travelers.length === 0 && saleNo && (
                  <p className="text-sm text-gray-500">등록된 여행객이 없습니다.</p>
                )}
                <div className="space-y-2">
                  {travelers.map((traveler, index) => (
                    <TravelerAccordionCard
                      key={traveler.key}
                      traveler={traveler}
                      index={index}
                      orderCancelled={isCancelled}
                      isExpanded={expandedKeys.includes(traveler.key)}
                      onToggle={() => {
                        setExpandedKeys((prev) =>
                          prev.includes(traveler.key)
                            ? prev.filter((k) => k !== traveler.key)
                            : [...prev, traveler.key]
                        );
                      }}
                      onUpdate={(updates) => {
                        setTravelers((prev) =>
                          prev.map((r) => (r.key === traveler.key ? { ...r, ...updates } : r))
                        );
                      }}
                      onDelete={() => {}}
                      onCancel={() => {}}
                      onRestore={() => {}}
                      editLocked={editLocked}
                      hideFooterActions
                      isSelected={false}
                      onSelect={() => {}}
                      allowPassportUpload={false}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <OtherCompanyOrderPricePanel
              orderInfo={orderInfo}
              loading={loading}
              error={error}
            />
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}
